/**
 * 무대(Stage) - 한 판이 서 있는 데 필요한 것 전부.
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 9.4절 · `이상한방-컨셉시트-v0.1.md` 4.1절(인당 배치 수)
 *
 * ## 무대를 다시 세울 수 있어야 했다
 *
 * 단계마다 **평면도가 바뀐다**(원룸 24셀 → 아파트 80셀). 그런데 셸·조명·컷어웨이·카메라 절두체가
 * 전부 `bounds`에 종속이라, 예전처럼 부트스트랩에서 한 번 만들고 끝낼 수 없다.
 *
 * 그래서 앱이 두 층으로 갈린다:
 *  - **살아남는 것** - 렌더러·씬·레이캐스터(`SceneCtx`), GLB 캐시, 프레임 루프, 오버레이 화면들
 *  - **단계마다 다시 세우는 것** - 이 파일의 `Stage`(평면도·셸·조명·격자·컷어웨이·카메라·세션·에디터)
 *
 * ## `ctx`를 다시 만드는 경로는 여기 없다 - 그게 이 파일이 갈라져 나온 이유다
 *
 * WebGL 컨텍스트를 단계마다 새로 만들면 브라우저의 컨텍스트 개수 상한(보통 8~16)에 몇 판 만에
 * 걸린다. 증상이 고약한 것은 **테스트로 잡히지 않고 실플레이 몇 판 뒤에야** 화면이 죽는다는 점이다.
 *
 * 그래서 규약을 문장이 아니라 **import 목록으로** 못박았다. 이 파일은 `render/scene`에서
 * `configureIsoCamera`와 `type SceneCtx`만 가져온다 - `createScene`을 부르지 않는 게 아니라
 * **가져오지도 않는다.** 무대를 몇 번을 다시 세우든 여기서 렌더러가 태어날 방법이 없고,
 * `ctx`는 언제나 인자로 들어온 남의 것이다. 그 하나뿐인 생성자는 `main.ts`에 있다
 * (`app/stage.test.ts`가 이 두 사실을 소스에서 검사한다).
 *
 * 카메라는 새로 만들지 않고 `configureIsoCamera`로 **제자리에서** 새 방 크기에 맞춘다.
 */

import type { SoundName } from '../audio/sounds'
import { CATALOG_BY_ID } from '../catalog/catalog'
import type { FloorPlan } from '../core/floorPlan'
import { cellToWorld } from '../core/grid'
import { CELL_SIZE_METERS, type PlayerId, type RoomBounds, type RoomConfig } from '../core/types'
import type { LevelDef } from '../game/levels'
import type { ClientView } from '../game/match/matchView'
import type { PlayerEvent } from '../game/match/matchTypes'
import { createGameSession, type ResultPinsView, type RoundOutcome } from '../game/session'
import type { Tuning } from '../game/tuning'
import type { MatchHost } from '../net/matchHost'
import type { Transport } from '../net/transport'
import type { ChangerTool } from '../input/changerTool'
import type { DecorateTool } from '../input/decorateTool'
import { createEditor } from '../input/editor'
import { createIsoCamera, type IsoCamera } from '../input/isoCamera'
import { createCutaway, type Cutaway } from '../render/cutaway'
import { createGridOverlay, setGridFocus } from '../render/gridHelper'
import { clearHitProxies } from '../render/hitProxy'
import { createLightingRig, disableShadowCasting } from '../render/lighting'
import { disposeObject3D } from '../render/objectView'
import { buildRoomShell, disposeRoomShell } from '../render/roomShell'
import { configureIsoCamera, type SceneCtx } from '../render/scene'
import { buildMatchStage } from './stageMatch'
import type { StageSession } from './stageSession'

/**
 * 배치 모드의 로컬 플레이어.
 * `placedBy`는 서버 전용 필드이므로(2.1절) 화면에 나오는 곳은 없고, 인당 배치 수 계산에만 쓰인다.
 * 생성기가 놓은 소품은 `'system'`이라 이 한도를 먹지 않는다.
 */
const LOCAL_PLAYER_ID = 'local-player'

/** 배치 모드의 인당 배치 한도(컨셉시트 4.1의 "인당 배치 수 제한"). 도구용이므로 넉넉히 둔다. */
const PLACEMENT_LIMIT = 48

/** 셀의 **바닥면** 월드 좌표. 격자는 그 셀이 놓이는 평면에 깔려야 하므로 중심이 아니라 바닥이다. */
function cellFloorWorld(x: number, y: number, z: number): { x: number; y: number; z: number } {
  const center = cellToWorld({ x, y, z })
  return { x: center.x, y: y * CELL_SIZE_METERS, z: center.z }
}

/** 한 판이 서 있는 데 필요한 것 전부. 평면도가 바뀌면 통째로 다시 세운다. */
export type Stage = Readonly<{
  plan: FloorPlan
  bounds: RoomBounds
  tuning: Tuning
  /** 이 무대가 어느 단계의 것인가. 자유 모드면 `null`. */
  levelId: number | null
  iso: IsoCamera
  cutaway: Cutaway
  /**
   * 라운드. 싱글(`GameSession`)이거나 여럿이(`MatchSession`)고, 손·개발 훅·앱 흐름은 둘의 공통 표면만 본다
   * (`stageSession.ts`). 어느 쪽인지 알아야 하는 자리는 `match`를 본다.
   */
  session: StageSession
  /** 여럿이일 때만. 개발 훅(`__weirdRoom.match`)이 뷰와 호스트 원본을 읽는 문이다 - 세션은 호스트를 읽지 않는다. */
  match: MatchStageHandle | null
  /** 배치 에디터. `?dev=1`이 없으면 `null`이며, 그때는 이 앱에 게임 모드밖에 없다. */
  editor: ReturnType<typeof createEditor> | null
  /**
   * 자기 구획을 고치는 도구(로드맵 M4 → M11: 전원이 든다). 바꾸기 페이즈에만 서고 그 밖에는 `null`이다 - 값이 아니라
   * 조회 함수인 이유는 무대가 살아 있는 동안 서고 사라지기 때문이다(`stageChanger.ts`). 손(`inputLayer`)이 탭의 뜻을 여기서 가른다.
   */
  changer: () => ChangerTool | null
  /**
   * 방을 함께 채우는 도구(M10). 꾸미기 페이즈에만 서고 그 밖에는 `null`이다 - `changer`와 같은 규약이고
   * 두 도구는 배타적인 페이즈에 선다. 손이 탭의 뜻을 이 둘로 가른다.
   */
  decorator: () => DecorateTool | null
  dispose: () => void
}>

export type MatchStageHandle = Readonly<{
  /** 마지막으로 받은 뷰. `__weirdRoom.match.view().found`에 `by`가 섞여 있는 것이 봇 판의 완료 판정이다(로드맵 M2). */
  view: () => ClientView | null
  /**
   * 루프백 호스트 원본. 콘솔에서 `state()`를 읽는 용도다 - 클라이언트 코드가 이것을 읽으면 그것이 곧 규칙 유출이다.
   * 소켓 방(M6)에서는 `null` - 원본은 서버에 있다.
   */
  host: MatchHost | null
  /** 세션이 말하는 문. 로비(`app/lobbyFlow.ts`)가 뷰를 읽으려고 구독한다 - 세션과 나란히, 규칙 없이. */
  transport: Transport
  /** 이벤트 하나를 세션의 번호열로 보낸다(`MatchSession.act`). 로비의 [준비]·[시작]·설정이 이 문을 지난다. */
  act: (event: PlayerEvent) => number
  /** 체인저 도구까지 접는다. 무대의 `dispose`가 세션과 함께 부른다. */
  dispose: () => void
}>

/**
 * 여럿이 무대의 요청(로드맵 M2 · M6) = **전송 주입 자리**. 누구와 말하는지는 무대가 모른다 - 봇 판은 `stageMatch.createLoopbackMatch`가
 * 같은 탭 안에 호스트를 세워 이 모양으로 넘기고, 소켓 방은 로비(`app/lobbyFlow.ts`)가 `welcome`을 받은 뒤 그 소켓을 넘긴다.
 * 무대는 둘을 구분하지 않는다(`MatchSession`이 `Transport`만 아는 것과 같은 선).
 */
export type MatchStageRequest = Readonly<{
  transport: Transport
  /** 내 좌석. 루프백은 고정값, 소켓 방은 `welcome.playerId`. */
  me: PlayerId
  /** 방 코드. 소켓 방은 방 시드를 모르므로 이것이 표시·개발 훅의 시드 자리를 대신한다. */
  roomCode: string
  /** 루프백 호스트. 소켓 방은 `null`. */
  host: MatchHost | null
  /**
   * 이 무대가 사라질 때 전송을 **닫지 않는다**(로드맵 M11). 기본은 닫는 것이다 - 전송은 세션의 것이다.
   *
   * 참으로 오는 자리는 하나뿐이다: 소켓 방의 **로비 뒤 임시 무대**(`app/lobbyFlow.ts`). 여럿이 판의 평면도는 좌석 수의
   * 함수라 로비에서 사람이 들락거리는 동안은 확정되지 않고, 판이 시작되는 순간(좌석 수가 굳는 순간) 무대를 다시 세워야 한다.
   * 그때 옛 무대가 소켓을 닫으면 방과의 연결이 끊긴다 - 그 한 자리에서만 소유권이 로비 쪽에 남는다.
   */
  retainTransport?: boolean
}>

/** 무대를 세우는 데 필요한 요청. 단계인지 자유 모드인지는 `level`의 유무로, 여럿이인지는 `match`의 유무로 갈린다. */
export type StageRequest = Readonly<{
  plan: FloorPlan
  tuning: Tuning
  seed: string
  /** 있으면 여럿이 - 그 전송 위에 `MatchSession`을 앉힌다. 없으면 싱글 `GameSession`이다. */
  match?: MatchStageRequest
  /** 시작 시도 번호. 방은 그대로이고 **변화만** 이 값에서 갈린다. 생략하면 첫 시도. */
  startAttempt?: number
  targetObjectCount?: number
  /**
   * 이 무대가 시작할 때 계정이 들고 있는 탄창 수. **배경 무대에도 준다** -
   * 라운드가 돌지 않아 쓸 일이 없지만, 여기서 빼면 "배경일 때만 0"이라는 예외가 하나 생긴다.
   */
  magazines?: number
  onMagazineUsed?: (remaining: number) => void
  /**
   * 여럿이 첫 바꾸기 안내(`ui/changerCoach.ts`)를 세울 것인가. **탄창과 같은 자리다** - 계정에 남는 값이라
   * 무대가 저장소를 읽지 않고 `main.ts`가 진행에서 읽어 넘긴다. 생략하면 세우지 않는다(싱글·배경 무대의 답이다).
   * 함수인 이유는 한 판이 여러 라운드를 돌기 때문이다 - 1라운드에 마친 안내가 2라운드에 다시 서면 안 된다.
   */
  needsCoach?: () => boolean
  /** 안내를 마쳤다. 계정에 남기는 것은 부르는 쪽의 몫이다(`onMagazineUsed`와 같은 방향). */
  onCoached?: () => void
  /**
   * 소리가 하나 났다는 알림(관측용). **재생을 대신하지 않는다** - 소리는 언제나
   * `audio/engine.ts`가 낸다(`session.ts`의 `SoundSink`). 여기로 받는 이유는 검증뿐이다.
   */
  onSound?: (name: SoundName, comboStep: number) => void
  level?: LevelDef | null
  hasNextLevel?: boolean
  /**
   * 방을 고정한다(매일의 방, 로드맵 M9). 결과 화면의 "한 판 더"가 같은 시드로 다시 세우고
   * "다른 변화로 다시"는 만들어지지 않는다 - 판단도 이유도 `game/session.ts`의 `lockedRoom`에 있다.
   */
  lockedRoom?: boolean
  startActive: boolean
  /** 배치 에디터를 함께 세울 것인가(`?dev=1`). 기본은 세우지 않는다. */
  withEditor?: boolean
  onSeedChange?: (seed: string) => void
  onAttemptChange?: (attempt: number) => void
  onRoundEnd?: (outcome: RoundOutcome) => void
  onNextLevel?: () => void
  onExitToSelect?: () => void
  /** 결과 화면의 핀 줄(로드맵 M3). `onRoundEnd`가 진행을 확정한 뒤 불리는 조회 함수다(`sessionResult.ts`). */
  resultPins?: () => ResultPinsView | null
}>

/**
 * 무대 하나를 세운다.
 *
 * 순서가 규약이다. **카메라 절두체를 먼저 새 방 크기에 맞춘다** - `createIsoCamera`가
 * "절두체가 이미 세워져 있다"를 전제로 던지기 때문이고, 그보다 중요하게는 절두체가 옛 방
 * 크기로 남으면 `zoom === 1`의 뜻(= 전체 조망)이 방마다 달라져 줌 감각이 단계마다 바뀐다.
 *
 * @param ctx 살아남는 층. **여기서 만들지 않고 받는다**(파일 머리말).
 */
export function buildStage(ctx: SceneCtx, request: StageRequest): Stage {
  const plan = request.plan
  const bounds = plan.bounds
  const canvas = ctx.renderer.domElement

  const aspect =
    canvas.clientWidth > 0 && canvas.clientHeight > 0 ? canvas.clientWidth / canvas.clientHeight : 1
  configureIsoCamera(ctx.camera, bounds, aspect)

  // 아이소메트릭은 항상 컷어웨이 상태다(천장 없음 · 내벽 허리 위 절단).
  const shell = buildRoomShell(ctx, plan, { cutaway: true })
  // 셸이 그림자를 지면 창광이 실내에 도달하지 못해 조명 리그가 통째로 무의미해진다(lighting.ts 계약).
  disableShadowCasting(shell.group)

  const lighting = createLightingRig(ctx.scene, plan)

  // createGridOverlay는 씬에 붙이지 않고 객체만 돌려준다(소유권을 호출부에 둔다).
  // 처음에는 숨겨져 있으며 배치 모드에서 지정한 셀 주위에만 나타난다.
  const gridOverlay = createGridOverlay(bounds)
  ctx.scene.add(gridOverlay)

  const iso = createIsoCamera(ctx, bounds)
  // 페이드를 자체 rAF로 굴리지 않고 프레임 루프에서 몰아 돌린다.
  // 시계가 둘이면 탭을 전환했다 돌아왔을 때 한쪽만 밀린 상태가 된다.
  const cutaway = createCutaway(shell.group, plan, { autoTick: false })

  /**
   * 라운드. 씬의 오브젝트는 **이 세션이 소유한다** - 원본↔변경본 교체가 전부 여기서 일어난다.
   * 생성자에서 곧바로 방을 씬에 올리므로, 앞선 무대가 남긴 소품 뷰도 이때 diff로 걷힌다.
   *
   * 요청을 **그대로 넘긴다.** 한때 이 자리는 선택 필드마다
   * `...(request.x === undefined ? {} : { x: request.x })` 한 줄이 붙은 아홉 줄이었다.
   * `exactOptionalPropertyTypes` 때문에 어쩔 수 없다고 적혀 있었지만, 그 플래그가 요구하는 것은
   * **받는 쪽의 선언**(`x?: T | undefined`)이지 넘기는 쪽의 삼항 연산자가 아니다.
   * 지금은 무엇을 넘기는지가 한 줄에 하나씩 그대로 보인다.
   */
  const { session, match, changer, decorator } = request.match === undefined
    ? { session: buildSoloSession(ctx, iso, plan, request), match: null, changer: () => null, decorator: () => null }
    : buildMatchStage(ctx, iso, plan, request, request.match)

  /** 배치 모드의 라운드 룰. 허용 동사는 튜닝이 원본이라 단계·URL이 에디터에도 그대로 먹는다(5.3절). */
  function buildRoomConfig(): RoomConfig {
    return {
      allowedVerbs: new Set(request.tuning.allowedVerbs),
      changeCount: request.tuning.changeCount,
      placementsPerPlayer: PLACEMENT_LIMIT,
    }
  }

  /**
   * 배치 에디터. 게임 모드에서는 꺼져 있고, 들어갈 때 `setRoom`으로 **화면의 방을 이어받는다**.
   * `initialRoom`을 세션의 방으로 주는 이유는 첫 프레임에 빈 방이 스치는 것을 막기 위해서다.
   *
   * `?dev=1`이 없으면 아예 만들지 않는다. 만들어 두고 숨기면 소품 목록 HUD가 단계마다
   * 문서에 한 벌씩 쌓이고, 그중 하나만 잘못 살아나도 라운드 중에 정답이 열린다.
   */
  const editor = request.withEditor !== true
    ? null
    : createEditor({
        ctx,
        bounds,
        catalog: CATALOG_BY_ID,
        config: buildRoomConfig(),
        playerId: LOCAL_PLAYER_ID,
        templateId: plan.id,
        hudMount: document.body,
        initialRoom: session.getVisibleRoom(),
        // 캔버스 포인터 이벤트는 제스처 인식기가 독점한다. 에디터가 같은 이벤트를 또 해석하면
        // 카메라를 끌려던 드래그가 배치로 확정된다(editor.ts의 `inputMode` 주석).
        inputMode: 'tap',
        onPointerCell: (cell) => {
          setGridFocus(gridOverlay, cell === null ? null : cellFloorWorld(cell.x, cell.y, cell.z))
        },
        startActive: false,
      })
  // 배치 HUD는 만들자마자 보이는 상태다. 게임 모드로 시작하므로 즉시 접는다.
  editor?.hud.setVisible(false)

  return {
    plan,
    bounds,
    tuning: request.tuning,
    levelId: request.level?.id ?? null,
    iso,
    cutaway,
    session,
    match,
    editor,
    changer,
    decorator,

    dispose(): void {
      // 체인저 도구(있으면)를 세션보다 먼저 접는다 - 도구의 외곽선은 씬에, 띠는 문서에 붙어 있다.
      match?.dispose()
      session.dispose()
      editor?.dispose()
      iso.dispose()
      cutaway.dispose()

      // Three.js는 GPU 자원을 GC 하지 않는다. 씬에서 빼는 것만으로는 지오메트리·머티리얼이 남는다.
      // 단계를 오갈 때마다 이 경로를 타므로, 빠뜨리면 열 판만 돌려도 VRAM이 계속 쌓인다.
      lighting.dispose()
      disposeRoomShell(shell)
      gridOverlay.removeFromParent()
      disposeObject3D(gridOverlay)
      // 프록시는 세션이 아니라 씬에 붙는다. 남겨 두면 다음 단계의 외우기 페이즈에 유령 타겟이 선다.
      clearHitProxies(ctx.scene)
    },
  }
}

/* --- 세션 ------------------------------------------------------------------------ */

/* 여럿이(`buildMatchStage`)는 `stageMatch.ts`에 있다 - 루프백 호스트·세션·체인저 도구의 조립. 여기는 싱글만 남았다. */

/** 싱글 라운드. 요청을 그대로 넘긴다(`buildStage` 안의 주석 - 삼항 연산자 아홉 줄이 사라진 이유). */
function buildSoloSession(ctx: SceneCtx, iso: IsoCamera, plan: FloorPlan, request: StageRequest): StageSession {
  return createGameSession({
    ctx,
    iso,
    bounds: plan.bounds,
    catalog: CATALOG_BY_ID,
    plan,
    tuning: request.tuning,
    seed: request.seed,
    mount: document.body,
    startActive: request.startActive,
    startAttempt: request.startAttempt,
    targetObjectCount: request.targetObjectCount,
    magazines: request.magazines,
    onMagazineUsed: request.onMagazineUsed,
    onSound: request.onSound,
    level: request.level ?? null,
    hasNextLevel: request.hasNextLevel ?? false,
    lockedRoom: request.lockedRoom,
    onSeedChange: request.onSeedChange,
    onAttemptChange: request.onAttemptChange,
    onRoundEnd: request.onRoundEnd,
    onNextLevel: request.onNextLevel,
    onExitToSelect: request.onExitToSelect,
    resultPins: request.resultPins,
  })
}
